import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from '../../i18n';
import { colors, primaryButtonSx, radius } from '../../theme/tokens';
import { copyToClipboard } from '../../utils/clipboard';
import type { RechargeResult } from '../../types/api';

/**
 * Modal do boleto: código de barras copiável e link para o PDF.
 *
 * Diferente do PIX, o boleto não tem polling — a compensação leva dias e o
 * legado também não acompanhava o pagamento aqui.
 */

interface BoletoDialogProps {
  data: RechargeResult;
  onClose: () => void;
}

const TITLE_ID = 'boleto-dialog-title';
/** Tempo que o botão fica exibindo "Copiado!", igual ao legado. */
const COPIED_FEEDBACK_MS = 2000;

export function BoletoDialog({ data, onClose }: BoletoDialogProps) {
  const { t } = useTranslation();

  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<number | null>(null);

  const barcode = data.boleto_codigo_barras ?? '';
  const boletoLink = data.boleto_link ?? '';

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current !== null) window.clearTimeout(copiedTimeoutRef.current);
    },
    []
  );

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(barcode);
    if (!ok) return;
    setCopied(true);
    if (copiedTimeoutRef.current !== null) window.clearTimeout(copiedTimeoutRef.current);
    copiedTimeoutRef.current = window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  }, [barcode]);

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: radius.lg } } }}
    >
      <DialogTitle
        id={TITLE_ID}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          fontSize: '1.25rem',
          fontWeight: 700,
        }}
      >
        {t.boleto.title}
        <IconButton aria-label={t.common.close} onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center' }}>
        <Typography sx={{ mb: 3, fontSize: '0.875rem', color: colors.textSecondary }}>
          {t.boleto.instructions}
        </Typography>

        <Box
          sx={{
            backgroundColor: colors.surfaceMuted,
            borderRadius: radius.sm,
            p: 2,
            mb: 3,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            fontWeight: 700,
            lineHeight: 1.6,
            wordBreak: 'break-all',
            userSelect: 'all',
          }}
        >
          {barcode}
        </Box>

        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <Button
            type="button"
            fullWidth
            onClick={handleCopy}
            startIcon={<ContentCopyIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              color: colors.textPrimary,
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: radius.sm,
              py: 1.25,
              '&:hover': { backgroundColor: colors.surfaceMuted },
            }}
          >
            <Box component="span" aria-live="polite">
              {copied ? t.common.copied : t.boleto.copyBarcode}
            </Box>
          </Button>

          {boletoLink ? (
            <Button
              component="a"
              href={boletoLink}
              target="_blank"
              rel="noopener noreferrer"
              fullWidth
              variant="contained"
              endIcon={<OpenInNewIcon />}
              sx={{ ...primaryButtonSx, py: 1.25 }}
            >
              {t.boleto.open}
            </Button>
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
